/**
 * Which pointing device the 3D view should assume.
 *
 * Shared by the camera bindings and the help dialog so the gesture the user is
 * told about is the gesture that is actually bound.
 */
export type Platform = 'windows' | 'mac' | 'touch'

export type PlatformSignals = {
  userAgent?: string
  /** navigator.userAgentData.platform, where the browser exposes it. */
  platformHint?: string
  /** True when the *primary* pointer is a finger rather than a mouse. */
  coarsePointer?: boolean
  maxTouchPoints?: number
}

const readSignals = (): PlatformSignals => {
  if (typeof navigator === 'undefined') return {}
  const uaData = (navigator as { userAgentData?: { platform?: string } })
    .userAgentData
  return {
    userAgent: navigator.userAgent,
    platformHint: uaData?.platform,
    // Reports the primary pointer, which is what matters: a touchscreen laptop
    // driven by a mouse answers `fine`, and should be given mouse gestures.
    coarsePointer:
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(pointer: coarse)').matches
        : undefined,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  }
}

/**
 * Which gestures this device should be shown and bound.
 *
 * Touch is decided on the *primary pointer* rather than on maxTouchPoints
 * alone. Plenty of Windows laptops have a touchscreen and report ten touch
 * points while being driven entirely by a trackpad; telling those users to
 * pinch and two-finger drag would be wrong.
 */
export const detectPlatform = (signals: PlatformSignals = {}): Platform => {
  const { userAgent, platformHint, coarsePointer, maxTouchPoints } = {
    ...readSignals(),
    ...signals,
  }

  const ua = userAgent ?? ''
  const hint = platformHint ?? ''

  // iPadOS identifies itself as a Mac, so tablets are settled first or every
  // iPad lands on trackpad instructions it cannot follow.
  if (/Android|iPad|iPhone|Silk/i.test(ua)) return 'touch'

  // Otherwise trust the primary pointer, falling back to the touch-point count
  // only where the media query is unavailable.
  const touchFirst = coarsePointer ?? (maxTouchPoints ?? 0) > 1
  if (touchFirst) return 'touch'

  return /Mac/i.test(hint) || /Mac|Macintosh/i.test(ua) ? 'mac' : 'windows'
}

/**
 * Tilt gestures for a Mac.
 *
 * Cesium binds tilt to the middle mouse button or Ctrl+drag. Neither suits a
 * Mac trackpad: there is no middle button, and Ctrl+click is the system
 * secondary-click, so Ctrl+drag is fighting the OS for the same gesture.
 * Option/Alt is unclaimed and is the conventional modifier for this on macOS.
 *
 * Cesium's own Ctrl bindings are kept rather than replaced: they do still work
 * with an external three-button mouse, and dropping them would take tilt away
 * from anyone already using one.
 */
export const macTiltEventTypes = (Cesium: any): unknown[] => {
  const { CameraEventType, KeyboardEventModifier } = Cesium
  return [
    CameraEventType.MIDDLE_DRAG,
    CameraEventType.PINCH,
    {
      eventType: CameraEventType.LEFT_DRAG,
      modifier: KeyboardEventModifier.ALT,
    },
    {
      eventType: CameraEventType.RIGHT_DRAG,
      modifier: KeyboardEventModifier.ALT,
    },
    {
      eventType: CameraEventType.LEFT_DRAG,
      modifier: KeyboardEventModifier.CTRL,
    },
    {
      eventType: CameraEventType.RIGHT_DRAG,
      modifier: KeyboardEventModifier.CTRL,
    },
  ]
}

/**
 * Bind the camera gestures this device should use.
 *
 * Only Macs need anything: Cesium's defaults already suit a Windows mouse and
 * a touch screen.
 */
export const applyPlatformCameraBindings = (
  scene: any,
  Cesium: any,
  platform: Platform = detectPlatform(),
): void => {
  const controller = scene?.screenSpaceCameraController
  if (!controller || !Cesium?.CameraEventType || platform !== 'mac') return

  controller.tiltEventTypes = macTiltEventTypes(Cesium)
}
